import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Clock, Building2, Users, Briefcase, FileText } from 'lucide-react';
import { getCompanyUsage, getMyCompany, getPlans } from '../api/companies';
import { getMyCustomQuotes } from '../api/customQuotes';
import { initiatePayment } from '../api/payments';
import { Button } from '../components/ui/Button';
import { PreCheckoutModal } from '../components/billing/PreCheckoutModal';
import { MissingBillingInfoModal } from '../components/billing/MissingBillingInfoModal';
import { PaymentModal } from '../components/billing/PaymentModal';

const FALLBACK_PLANS = [
  { name: 'starter',  display_name: 'Starter',  price_monthly: 1199, price_yearly: 11510, max_users: 5 },
  { name: 'business', display_name: 'Business', price_monthly: 1999, price_yearly: 19190, max_users: 20 },
  { name: 'pro',      display_name: 'Pro',      price_monthly: 2999, price_yearly: 28790, max_users: 50 },
];

export function BillingPage() {
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  const [company, setCompany] = useState(null);
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [customQuote, setCustomQuote] = useState(null);

  // Modal state
  const [preCheckoutOpen, setPreCheckoutOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [missingBillingOpen, setMissingBillingOpen] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [iframeUrl, setIframeUrl] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    getCompanyUsage().then(setUsage).catch(() => {});
    getMyCompany().then(d => setCompany(d.company)).catch(() => {});
    getPlans().then(d => {
      if (d.plans?.length) setPlans(d.plans);
    }).catch(() => {});
    getMyCustomQuotes().then(d => {
      const pending = d.quotes?.find(q => q.status === 'pending' && new Date(q.valid_until) > new Date());
      if (pending) setCustomQuote(pending);
    }).catch(() => {});
  }, []);

  const handleSelectPlan = (plan) => {
    const required = [
      'billing_name',
      'billing_tax_number',
      'billing_tax_office',
      'billing_address',
      'billing_city',
      'billing_district',
    ];

    const missing = required.filter((field) => {
      const value = company?.[field];
      return !value || String(value).trim().length === 0;
    });

    if (missing.length > 0) {
      setSelectedPlan(plan);
      setMissingFields(missing);
      setMissingBillingOpen(true);
      setError('');
      return;
    }

    setSelectedPlan(plan);
    setPreCheckoutOpen(true);
    setError('');
  };

  const handlePreCheckoutConfirm = async (consentId) => {
    setPreCheckoutOpen(false);

    try {
      const result = await initiatePayment({
        plan_name: selectedPlan.name,
        billing_cycle: billingCycle,
        legal_consent_id: consentId,
        custom_quote_id: selectedPlan.name === 'enterprise' ? customQuote?.id : undefined,
      });

      setIframeUrl(result.iframeUrl);
      setPaymentOpen(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Ödeme başlatılamadı');
    }
  };

  const handlePaymentSuccess = () => {
    navigate('/odeme/basarili');
  };

  const handlePaymentFail = () => {
    navigate('/odeme/basarisiz');
  };

  const formatTL = (n) =>
    new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(n);

  const statusLabels = {
    trial: 'Deneme', active: 'Aktif', suspended: 'Askıda', cancelled: 'İptal',
  };
  const statusColors = {
    trial: 'bg-blue-100 text-blue-700', active: 'bg-green-100 text-green-700',
    suspended: 'bg-red-100 text-red-700', cancelled: 'bg-slate-100 text-slate-700',
  };

  // KDV hariç tutar
  const getPlanPrice = (plan) => {
    return billingCycle === 'yearly'
      ? parseFloat(plan.price_yearly || plan.price_monthly * 12)
      : parseFloat(plan.price_monthly);
  };

  // KDV dahil toplam
  const getPlanTotal = (plan) => getPlanPrice(plan) * 1.20;

  // Custom quote için KDV dahil toplam
  const getCustomQuoteTotal = (quote) => {
    const base = billingCycle === 'yearly'
      ? parseFloat(quote.yearly_amount || quote.monthly_amount * 12)
      : parseFloat(quote.monthly_amount);
    return base * 1.20;
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 mb-1">Fatura & Plan</h1>
          <p className="text-slate-500 text-sm">Abonelik yönetimi ve ödeme ayarları</p>
        </div>
        <Button variant="secondary" onClick={() => navigate('/faturalar')}>
          <FileText size={16} /> Faturalarım
        </Button>
      </div>

      {/* Mevcut plan özeti — KORUNDU */}
      {usage && company && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-3">
                <Building2 size={20} className="text-slate-400" />
                <h2 className="text-lg font-semibold text-slate-800">{company.name}</h2>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusColors[usage.subscription_status]}`}>
                  {statusLabels[usage.subscription_status]}
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-1">
                Plan: <strong className="text-slate-700 capitalize">{usage.plan}</strong>
              </p>
            </div>
            {usage.subscription_status === 'trial' && usage.trial_days_left !== null && (
              <div className="text-right">
                <div className="flex items-center gap-1.5 text-sm text-blue-600">
                  <Clock size={14} />
                  <span className="font-semibold">{usage.trial_days_left} gün kaldı</span>
                </div>
                <p className="text-xs text-slate-400 mt-0.5">Deneme süresi</p>
              </div>
            )}
          </div>
          <div className="grid grid-cols-3 gap-4 mt-4">
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="flex items-center gap-2 text-slate-500 text-xs mb-1"><Users size={12} /> Kullanıcı</div>
              <p className="text-lg font-bold text-slate-800">{usage.user_count} <span className="text-sm font-normal text-slate-400">/ {usage.max_users}</span></p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="flex items-center gap-2 text-slate-500 text-xs mb-1"><Briefcase size={12} /> Toplam İş</div>
              <p className="text-lg font-bold text-slate-800">{usage.job_count}</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3">
              <div className="flex items-center gap-2 text-slate-500 text-xs mb-1"><Building2 size={12} /> Müşteri</div>
              <p className="text-lg font-bold text-slate-800">{usage.customer_count}</p>
            </div>
          </div>
        </div>
      )}

      {/* Plan seçimi */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-800">
          {usage?.subscription_status === 'active' ? 'Plan Değiştir' : 'Plan Seçin'}
        </h3>

        {/* Aylık/Yıllık toggle */}
        <div className="bg-slate-100 rounded-lg p-1 flex">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-3 py-1.5 text-sm rounded-md transition ${billingCycle === 'monthly' ? 'bg-white shadow-sm font-semibold text-slate-800' : 'text-slate-500'}`}
          >
            Aylık
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={`px-3 py-1.5 text-sm rounded-md transition ${billingCycle === 'yearly' ? 'bg-white shadow-sm font-semibold text-slate-800' : 'text-slate-500'}`}
          >
            Yıllık <span className="text-green-600 ml-0.5">(%20)</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 mb-4">
          {error}
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-5 mb-8">
        {plans.filter(p => p.name !== 'enterprise').map(plan => {
          const isCurrent = usage?.plan === plan.name && usage?.subscription_status === 'active';
          const popular = plan.name === 'business';
          const price = getPlanPrice(plan);
          const monthlyEq = billingCycle === 'yearly' ? price / 12 : price;

          return (
            <div key={plan.name} className={`relative rounded-xl border-2 p-6 ${isCurrent ? 'border-blue-500 bg-blue-50/50' : 'border-slate-200'}`}>
              {popular && (
                <span className="absolute -top-2.5 left-4 px-2.5 py-0.5 bg-blue-600 text-white text-[10px] font-bold rounded-full">Popüler</span>
              )}
              <h4 className="text-lg font-bold text-slate-800">{plan.display_name}</h4>
              <p className="text-sm text-slate-500">{plan.max_users} kullanıcı</p>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-3xl font-extrabold text-slate-900">{formatTL(monthlyEq)}</span>
                <span className="text-slate-500 text-sm">/ay</span>
              </div>
              {billingCycle === 'yearly' && (
                <p className="text-xs text-slate-500 mt-1">
                  Yıllık {formatTL(price)} (KDV hariç)
                </p>
              )}
              <p className="text-xs text-slate-400 mt-1">+ KDV %20</p>

              <div className="mt-4">
                {isCurrent ? (
                  <div className="flex items-center gap-1.5 text-sm text-blue-600 font-medium">
                    <Check size={16} /> Mevcut Planınız
                  </div>
                ) : (
                  <Button
                    variant={popular ? 'primary' : 'secondary'}
                    className="w-full justify-center"
                    onClick={() => handleSelectPlan(plan)}
                  >
                    Plan Seç
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Enterprise / Custom Quote */}
      {customQuote && (
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-xl p-6 mb-8">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-lg font-bold">Size Özel Teklif</h4>
            <span className="text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full font-semibold">
              {Math.ceil((new Date(customQuote.valid_until) - Date.now()) / (1000 * 60 * 60 * 24))} gün geçerli
            </span>
          </div>
          {customQuote.description && (
            <p className="text-sm text-slate-300 mb-3">{customQuote.description}</p>
          )}
          <p className="text-sm mb-4">
            <span className="text-slate-400">Tutar:</span>
            <span className="font-bold text-2xl ml-2">
              {formatTL(billingCycle === 'yearly'
                ? (customQuote.yearly_amount || customQuote.monthly_amount * 12)
                : customQuote.monthly_amount)}
            </span>
            <span className="text-slate-400 text-xs ml-1">
              /{billingCycle === 'yearly' ? 'yıl' : 'ay'} (KDV hariç)
            </span>
          </p>
          <Button
            onClick={() => handleSelectPlan({ name: 'enterprise', display_name: 'Enterprise (Özel)' })}
          >
            Bu Teklifi Kabul Et ve Öde
          </Button>
        </div>
      )}

      {!customQuote && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 text-center">
          <h4 className="text-lg font-semibold text-slate-800 mb-2">Enterprise / Özel Teklif</h4>
          <p className="text-sm text-slate-600 mb-3">
            Daha fazla kullanıcı veya özel ihtiyaçlar için bize ulaşın.
          </p>
          <a href="mailto:destek@fieldpin.io" className="text-blue-600 hover:underline text-sm font-medium">
            destek@fieldpin.io
          </a>
        </div>
      )}

      {/* Modallar */}
      <PreCheckoutModal
        open={preCheckoutOpen}
        onClose={() => setPreCheckoutOpen(false)}
        onConfirm={handlePreCheckoutConfirm}
        planLabel={selectedPlan ? `${selectedPlan.display_name} (${billingCycle === 'yearly' ? 'Yıllık' : 'Aylık'})` : ''}
        totalAmount={selectedPlan
          ? (selectedPlan.name === 'enterprise' && customQuote
              ? getCustomQuoteTotal(customQuote)
              : getPlanTotal(selectedPlan))
          : 0}
      />

      <PaymentModal
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        iframeUrl={iframeUrl}
        onSuccess={handlePaymentSuccess}
        onFail={handlePaymentFail}
      />

      <MissingBillingInfoModal
        open={missingBillingOpen}
        onClose={() => setMissingBillingOpen(false)}
        missingFields={missingFields}
      />
    </div>
  );
}
